/**
 * vqeStore — transient UI state for the Visual Query Editor.
 *
 * Per `Ralph/VisualQueryEditorReadOnly/PRD.md` §State flow: this store holds
 * only ephemeral UI state (active buffer pointer, render mode, expansion,
 * focus, queued harness reports). The buffer
 * is the source of truth and is persisted by the Query Editor's existing
 * persistence — vqeStore never persists to disk.
 *
 * Undo is the Query tab's own Monaco history — this store keeps none.
 *
 * `lastExecuted` (Plan 6 §3.3 S8, decision 5) records the exact statement text
 * dispatched by the tab's most recent run, keyed by tabId, so the "results are
 * stale" marker survives tab switches/remounts.
 * Follows the store's existing flat-record, no-cleanup precedent (like
 * `expansion` / `whereViewMode` / `dismissedBanners`) — entries are never
 * pruned on tab close.
 */

import { create } from 'zustand';
import type {
  DrillEntry,
  HarnessReport,
  RenderMode,
  VqeActiveBuffer,
  VqeFocus,
} from '../types';

/**
 * Per-tab readiness-banner dismissal key. The readiness banner remembers, per
 * tab session, that the user already dismissed it — so it teaches once and then
 * stays quiet. Currently keyed by `tabId`; typed as a plain string so future
 * banner kinds can compose `${tabId}:${kind}` without a model change.
 */
export type BannerKey = string;

/** Per-WHERE-clause display mode. `auto` defers to the depth/size heuristic. */
export type WhereViewMode = 'auto' | 'inline' | 'rail';

/**
 * Commit-feedback payload recorded by `commitEdit` after the SR-Edit
 * apply→emit→setValue pipeline runs. `kind` distinguishes a successful buffer
 * write from a caught failure; `at` is a fresh `Date.now()` timestamp that makes
 * each payload a distinct object so the toast hook's last-seen guard fires once
 * per commit. `message` carries the failure reason (success omits it).
 * `documentId` names the SR-Edit document the edit landed in, so the surface
 * that made it (full tab or compact popover) can show its own "Edit applied ·
 * Undo" status and every other mounted surface stays quiet.
 */
export type VqeCommitFeedback = {
  readonly kind: 'success' | 'failure';
  readonly at: number;
  readonly message?: string;
  readonly documentId?: string;
};

export type VqeState = {
  activeBuffer: VqeActiveBuffer | null;
  renderMode: RenderMode;
  expansion: Record<string, boolean>;
  focus: VqeFocus | null;
  pendingReports: HarnessReport[];
  drillStack: DrillEntry[];
  /** Readiness banners the user has dismissed this session (transient, per-tab). */
  dismissedBanners: ReadonlySet<BannerKey>;
  /** Per-(document, statement) pinned WHERE view mode (transient, keyed). */
  whereViewMode: Record<string, WhereViewMode>;
  /**
   * Whether the compact popover's live SQL preview is collapsed. The preview
   * is open by default (prim #1 §2); once a user collapses it, it stays
   * collapsed for the rest of the session. Transient — never persisted to disk
   * (a fresh app launch re-opens the preview). A single session-global flag,
   * matching the "for the session" intent (not per-tab/per-statement).
   */
  previewCollapsed: boolean;
  /**
   * Most recent commit-feedback payload (or `null` before the first commit).
   * Failures surface as a toast (`useCommitFeedbackToasts`); successes as the
   * committing surface's inline "Edit applied · Undo" status
   * (`useEditAppliedStatus`).
   */
  commitFeedback: VqeCommitFeedback | null;
  /**
   * Statement text dispatched by the most recent execution, keyed by tabId
   * (Plan 6 §3.3 S8, decision 5). Drives the results-stale marker.
   */
  lastExecuted: Readonly<Record<string, string>>;

  setActiveBuffer: (buffer: VqeActiveBuffer | null) => void;
  /** Record the outcome of a commit attempt (drives the failure toast and the success status). */
  recordCommitFeedback: (feedback: VqeCommitFeedback) => void;
  /** Mark a readiness banner dismissed for this session (idempotent, immutable). */
  dismissBanner: (key: BannerKey) => void;
  queueReport: (report: HarnessReport) => void;
  setRenderMode: (mode: RenderMode) => void;
  setExpansion: (key: string, open: boolean) => void;
  setWhereViewMode: (key: string, mode: WhereViewMode) => void;
  /** Set the compact preview collapsed pref (session-transient). */
  setPreviewCollapsed: (collapsed: boolean) => void;
  setFocus: (focus: VqeFocus | null) => void;
  pushDrill: (entry: DrillEntry) => void;
  /** Remove the tab's most recent frame (every tab's frames share one array). */
  popDrill: (tabId: string) => void;
  /** Clear the tab's frames. */
  clearDrill: (tabId: string) => void;
  /** Record the statement text dispatched by an execution for this tab. */
  setLastExecuted: (tabId: string, text: string) => void;
};

export const initialVqeState: Pick<
  VqeState,
  | 'activeBuffer'
  | 'renderMode'
  | 'expansion'
  | 'focus'
  | 'pendingReports'
  | 'drillStack'
  | 'dismissedBanners'
  | 'whereViewMode'
  | 'previewCollapsed'
  | 'commitFeedback'
  | 'lastExecuted'
> = {
  activeBuffer: null,
  renderMode: 'parse-error',
  expansion: {},
  focus: null,
  pendingReports: [],
  drillStack: [],
  dismissedBanners: new Set<BannerKey>(),
  whereViewMode: {},
  previewCollapsed: false,
  commitFeedback: null,
  lastExecuted: {},
};

export const useVqeStore = create<VqeState>((set) => ({
  ...initialVqeState,

  setActiveBuffer: (activeBuffer) => set({ activeBuffer }),

  recordCommitFeedback: (commitFeedback) => set({ commitFeedback }),

  dismissBanner: (key) =>
    set((state) =>
      state.dismissedBanners.has(key)
        ? state
        : { dismissedBanners: new Set([...state.dismissedBanners, key]) },
    ),

  queueReport: (report) =>
    set((state) => ({ pendingReports: [...state.pendingReports, report] })),

  setRenderMode: (renderMode) => set({ renderMode }),

  setExpansion: (key, open) =>
    set((state) => ({ expansion: { ...state.expansion, [key]: open } })),

  setWhereViewMode: (key, mode) =>
    set((state) => ({ whereViewMode: { ...state.whereViewMode, [key]: mode } })),

  setPreviewCollapsed: (previewCollapsed) => set({ previewCollapsed }),

  setFocus: (focus) => set({ focus }),

  pushDrill: (entry) =>
    set((state) => ({ drillStack: [...state.drillStack, entry] })),

  popDrill: (tabId) =>
    set((state) => {
      const index = state.drillStack.map((entry) => entry.tabId).lastIndexOf(tabId);
      return { drillStack: state.drillStack.filter((_, i) => i !== index) };
    }),

  clearDrill: (tabId) =>
    set((state) => ({ drillStack: state.drillStack.filter((entry) => entry.tabId !== tabId) })),

  setLastExecuted: (tabId, text) =>
    set((state) => ({ lastExecuted: { ...state.lastExecuted, [tabId]: text } })),
}));

export function resetVqeStoreForTests(): void {
  useVqeStore.setState({ ...initialVqeState });
}
